import { useContext, useState } from 'react'
import { Link as RouterLink, useLocation, useParams } from 'react-router'
import { LogoutOutlined, Menu as MenuIcon, PersonOutlineOutlined, SettingsOutlined, ShieldOutlined } from '@mui/icons-material'
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'

import meshifyLogo from '../../images/meshify-logo.svg'
import protectMobileLogo from '../../images/protect-icon-logo.svg'
import protectMobileLogo2 from '../../images/protect-icon-logo-2.svg'
import { useActivationContext } from '../../providers/activation-provider'
import { AuthenticationContext } from '../../providers/auth-provider'
import { NavContext } from '../../providers/nav-provider'
import { SHORT_SCREEN_HEIGHT } from '../../utils/constants'
import useIsMobile from '../../utils/hooks/useIsMobile'
import ActivateWithAppDialog from '../activate-with-app-dialog'
import ChangePasswordModal from '../change-password-modal'
import ContactDetailsModal from '../contact-details-modal'
import LinearProgress from '../m3/linear-progress'
import SettingsModal from '../settings-modal'

import useStyles from './index.styles'

interface IProps {
  title?: string | React.ReactNode
  centerHeader?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  rightIconMobile?: React.ReactNode
  useDarkBackground?: boolean
  progress?: number
  showLogo?: boolean
  showLogoMobile?: boolean
  showHamburger?: boolean
  /** Shows the user initials button and its dropdown menu, including on mobile. By default it only shows on desktop when `showLogo` or `showHamburger` is set */
  showUserMenu?: boolean
}

const Header = (props: IProps) => {
  const {
    title,
    centerHeader = true,
    leftIcon,
    rightIcon,
    rightIconMobile,
    useDarkBackground = false,
    progress,
    showLogo,
    showLogoMobile,
    showHamburger,
    showUserMenu,
  } = props

  const classes = useStyles
  const theme = useTheme()

  const { ownUser, handleLogout } = useContext(AuthenticationContext)
  const { toggleSidenav } = useContext(NavContext)

  const isShortScreen = useMediaQuery(`(max-height:${SHORT_SCREEN_HEIGHT})`)
  const isMobile = useIsMobile()

  const location = useLocation()
  const { folderId: folderIdParam } = useParams()
  const { activationLocationState } = useActivationContext()
  const { folderId: folderIdActivationState } = activationLocationState
  const folderId = folderIdParam ? Number(folderIdParam) : folderIdActivationState > 0 ? folderIdActivationState : undefined
  const dashboardTab = location.pathname.match(/^\/dashboard\/\d+\/(devices|alerts|contacts|order-history|help)/)?.[1]

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const [showContactDetailsModal, setShowContactDetailsModal] = useState(false)
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [showActivateWithAppDialog, setShowActivateWithAppDialog] = useState(false)

  const listItemTextSlotProps = { primary: { sx: { color: theme.palette.text.primary } } }

  const showBorderBottom = location.pathname !== '/login'
  const isMobileHeaderLayout =
    isMobile &&
    (location.pathname.startsWith('/dashboard') ||
      location.pathname === '/alerts' ||
      location.pathname === '/contacts' ||
      location.pathname === '/order-history' ||
      location.pathname === '/help')
  const showPrimaryLogo = (showLogo && !isMobile) || (showLogoMobile && isMobile)
  const primaryLogoSrc = showLogoMobile && isMobile ? protectMobileLogo : meshifyLogo
  const primaryLogoAlt = showLogoMobile && isMobile ? 'Protect Mobile Logo' : 'Meshify Logo'
  const showUserMenuButton = !!ownUser && (showUserMenu || (!isMobile && (showLogo || showHamburger)))

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleContactDetailsClick = () => {
    setShowContactDetailsModal(true)
    handleClose()
  }

  const handleSecurityClick = () => {
    setShowChangePasswordModal(true)
    handleClose()
  }

  const handleSettingsClick = () => {
    setShowSettingsModal(true)
    handleClose()
  }

  const handleSignOutClick = () => {
    handleLogout()
  }

  const getMobilePageTitle = (): string => {
    if (!location.pathname.startsWith('/dashboard')) {
      switch (location.pathname) {
        case '/alerts':
          return 'Alerts'
        case '/contacts':
          return 'Contacts'
        case '/order-history':
          return 'Order History'
        case '/help':
          return 'Help & Support'
        default:
          return ''
      }
    }

    if (!dashboardTab || dashboardTab === 'devices') return 'Devices'
    if (dashboardTab === 'alerts') return 'Alerts'
    if (dashboardTab === 'contacts') return 'Contacts'
    if (dashboardTab === 'order-history') return 'Order History'
    if (dashboardTab === 'help') return 'Help & Support'
    return ''
  }

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        ...classes.headerAppBar(theme),
        ...(showBorderBottom ? classes.headerAppBarWithBorder(theme) : {}),
        ...(useDarkBackground ? classes.headerAppBarDark(theme) : {}),
        ...(isShortScreen && !dashboardTab ? { paddingTop: 0 } : {}),
      }}
    >
      <Toolbar
        sx={[
          classes.headerToolbar,
          centerHeader && classes.headerToolbarCentered,
          centerHeader && isMobile && (!title ? { justifyContent: 'flex-start' } : { justifyContent: 'center' }),
        ]}
      >
        {showHamburger && !isMobile && (
          <IconButton edge="start" onClick={toggleSidenav} aria-label="Toggle navigation" sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>
        )}

        {showPrimaryLogo && (
          <RouterLink to={folderId ? `/dashboard/${folderId}/devices` : '/dashboard'}>
            <img src={primaryLogoSrc} alt={primaryLogoAlt} style={classes.logo} />
          </RouterLink>
        )}

        {isMobileHeaderLayout && (
          <>
            {leftIcon || (
              <RouterLink to={folderId ? `/dashboard/${folderId}/devices` : '/dashboard'}>
                <img src={protectMobileLogo} alt="Protect Mobile Logo" style={classes.logo} />
              </RouterLink>
            )}

            <Typography variant="h6" sx={classes.pageTitle(useDarkBackground, theme)}>
              {getMobilePageTitle()}
            </Typography>

            {rightIconMobile || <Box sx={classes.iconSpacer} />}
          </>
        )}

        {!showLogo && !isMobileHeaderLayout && (
          <>
            {leftIcon ? <>{leftIcon}</> : rightIcon || (isMobile && showUserMenuButton) ? <Box sx={classes.spacer} /> : null}{' '}
            {/* Spacer to balance the right icon and center the title */}
            {typeof title === 'string' ? (
              <Typography variant="h6" sx={classes.pageTitle(useDarkBackground, theme)}>
                {title}
              </Typography>
            ) : (
              title
            )}
            {rightIcon ? <>{rightIcon}</> : leftIcon && !rightIcon ? <Box sx={classes.spacer} /> : null}{' '}
            {/* Spacer to balance the left icon and center the title */}
          </>
        )}

        <Grid container sx={{ alignItems: 'center', justifyContent: 'flex-end', flex: 1, gap: 1 }}>
          {!isMobile && (
            <Button variant="contained" color="secondary" disableElevation onClick={() => setShowActivateWithAppDialog(true)} sx={classes.downloadAppButton}>
              <img src={protectMobileLogo2} alt="" width={16} height={16} />
              Download Mobile App
            </Button>
          )}
          {showUserMenuButton && (
            <>
              <Button
                onClick={handleClick}
                startIcon={<Avatar sx={classes.userAvatar(theme)}>{ownUser?.information.first?.[0]?.toUpperCase()}</Avatar>}
                sx={classes.userButton(theme, useDarkBackground)}
              >
                {!isMobile && (
                  <>
                    {ownUser?.information.first} {ownUser?.information.last}
                  </>
                )}
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                slotProps={{
                  list: {
                    'aria-labelledby': 'header-menu-button',
                  },
                }}
              >
                <MenuList sx={classes.menuList}>
                  <MenuItem onClick={handleContactDetailsClick} dense={true}>
                    <ListItemIcon>
                      <PersonOutlineOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText slotProps={listItemTextSlotProps}>Contact Details</ListItemText>
                  </MenuItem>

                  <MenuItem onClick={handleSecurityClick} dense={true}>
                    <ListItemIcon>
                      <ShieldOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText slotProps={listItemTextSlotProps}>Security</ListItemText>
                  </MenuItem>

                  <MenuItem onClick={handleSettingsClick} dense={true}>
                    <ListItemIcon>
                      <SettingsOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText slotProps={listItemTextSlotProps}>Settings</ListItemText>
                  </MenuItem>

                  <MenuItem onClick={handleSignOutClick} dense={true}>
                    <ListItemIcon>
                      <LogoutOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText slotProps={listItemTextSlotProps}>Sign out</ListItemText>
                  </MenuItem>
                </MenuList>
              </Menu>
            </>
          )}
        </Grid>
      </Toolbar>

      {typeof progress === 'number' && <LinearProgress variant="determinate" value={progress} />}

      {showContactDetailsModal && ownUser && (
        <ContactDetailsModal isOpen={showContactDetailsModal} onClose={() => setShowContactDetailsModal(false)} user={ownUser} selectedFolderId={folderId} />
      )}

      <ChangePasswordModal open={showChangePasswordModal} onClose={() => setShowChangePasswordModal(false)} />

      <SettingsModal open={showSettingsModal} onClose={() => setShowSettingsModal(false)} />

      <ActivateWithAppDialog open={showActivateWithAppDialog} onClose={() => setShowActivateWithAppDialog(false)} />
    </AppBar>
  )
}

export default Header
